import React from 'react';
import { useTranslation } from 'react-i18next';

import PolicyNavigation from './PolicyNavigation';

interface PrivacyPolicyContentProps {
  onNavigate?: (view: string) => void;
}

const PrivacyPolicyContent: React.FC<PrivacyPolicyContentProps> = ({ onNavigate }) => {
  const { t } = useTranslation();
  return (
    <div className="max-w-4xl mx-auto vscode-text-primary pt-8">
      <h1 className="text-3xl font-bold mb-6 vscode-text-primary">{t('legal.privacyTitle')}</h1>
      
      <div className="prose max-w-none">
        <p className="text-sm vscode-text-secondary mb-6">
          <strong>{t('legal.privacy.effectiveDate')}:</strong> {t('legal.privacy.effectiveDateValue')}<br />
          <strong>{t('legal.privacy.lastUpdated')}:</strong> {t('legal.privacy.lastUpdatedValue')}
        </p>

        <p className="text-lg vscode-text-secondary mb-6">{t('legal.privacy.intro')}</p>

        <p className="vscode-text-secondary mb-4">{t('legal.privacy.intro2')}</p>

        <p className="vscode-text-secondary mb-6">{t('legal.privacy.intro3')}</p>

        {/* Section 1 */}
        <h2 className="text-2xl font-semibold mt-8 mb-4">{t('legal.privacy.s1Title')}</h2>
        
        <h3 className="text-xl font-semibold mt-6 mb-3">{t('legal.privacy.s1_1Title')}</h3>
        <p className="vscode-text-secondary mb-4">{t('legal.privacy.s1_1Intro')}</p>
        <ul className="list-disc list-inside vscode-text-secondary mb-4 space-y-2 ml-4">
          <li>{t('legal.privacy.personal1')}</li>
          <li>{t('legal.privacy.personal2')}</li>
          <li>{t('legal.privacy.personal3')}</li>
          <li>{t('legal.privacy.personal4')}</li>
          <li>{t('legal.privacy.personal5')}</li>
          <li>{t('legal.privacy.personal6')}</li>
        </ul>

        <h3 className="text-xl font-semibold mt-6 mb-3">{t('legal.privacy.s1_2Title')}</h3>
        <p className="vscode-text-secondary mb-4">{t('legal.privacy.s1_2Intro')}</p>
        <ul className="list-disc list-inside vscode-text-secondary mb-4 space-y-2 ml-4">
          <li>{t('legal.privacy.auto1')}</li>
          <li>{t('legal.privacy.auto2')}</li>
          <li>{t('legal.privacy.auto3')}</li>
          <li>{t('legal.privacy.auto4')}</li>
        </ul>

        <h3 className="text-xl font-semibold mt-6 mb-3">{t('legal.privacy.s1_3Title')}</h3>
        <p className="vscode-text-secondary mb-4">{t('legal.privacy.s1_3Intro')}</p>
        <ul className="list-disc list-inside vscode-text-secondary mb-4 space-y-2 ml-4">
          <li>{t('legal.privacy.jobApp1')}</li>
          <li>{t('legal.privacy.jobApp2')}</li>
          <li>{t('legal.privacy.jobApp3')}</li>
          <li>{t('legal.privacy.jobApp4')}</li>
          <li>{t('legal.privacy.jobApp5')}</li>
        </ul>

        <h3 className="text-xl font-semibold mt-6 mb-3">{t('legal.privacy.s1_4Title')}</h3>
        <p className="vscode-text-secondary mb-4">{t('legal.privacy.s1_4Intro')}</p>
        <ul className="list-disc list-inside vscode-text-secondary mb-4 space-y-2 ml-4">
          <li>{t('legal.privacy.employer1')}</li>
          <li>{t('legal.privacy.employer2')}</li>
          <li>{t('legal.privacy.employer3')}</li>
          <li>{t('legal.privacy.employer4')}</li>
          <li>{t('legal.privacy.employer5')}</li>
          <li>{t('legal.privacy.employer6')}</li>
          <li>{t('legal.privacy.employer7')}</li>
          <li>{t('legal.privacy.employer8')}</li>
          <li>{t('legal.privacy.employer9')}</li>
        </ul>

        <h3 className="text-xl font-semibold mt-6 mb-3">{t('legal.privacy.s1_5Title')}</h3>
        <p className="vscode-text-secondary mb-4">{t('legal.privacy.s1_5Intro')}</p>
        <ul className="list-disc list-inside vscode-text-secondary mb-4 space-y-2 ml-4">
          <li>{t('legal.privacy.payment1')}</li>
          <li>{t('legal.privacy.payment2')}</li>
          <li>{t('legal.privacy.payment3')}</li>
          <li>{t('legal.privacy.payment4')}</li>
        </ul>

        {/* Section 2 */}
        <h2 className="text-2xl font-semibold mt-8 mb-4">{t('legal.privacy.s2Title')}</h2>
        
        <h3 className="text-xl font-semibold mt-6 mb-3">{t('legal.privacy.s2_1Title')}</h3>
        <p className="vscode-text-secondary mb-4">{t('legal.privacy.s2_1Intro')}</p>
        <ul className="list-disc list-inside vscode-text-secondary mb-4 space-y-2 ml-4">
          <li>{t('legal.privacy.use1')}</li>
          <li>{t('legal.privacy.use2')}</li>
          <li>{t('legal.privacy.use3')}</li>
          <li>{t('legal.privacy.use4')}</li>
          <li>{t('legal.privacy.use5')}</li>
          <li>{t('legal.privacy.use6')}</li>
          <li>{t('legal.privacy.use7')}</li>
          <li>{t('legal.privacy.use8')}</li>
        </ul>

        <h3 className="text-xl font-semibold mt-6 mb-3">{t('legal.privacy.s2_2Title')}</h3>
        <p className="vscode-text-secondary mb-4">{t('legal.privacy.s2_2Intro')}</p>
        <ul className="list-disc list-inside vscode-text-secondary mb-4 space-y-2 ml-4">
          <li>{t('legal.privacy.seeker1')}</li>
          <li>{t('legal.privacy.seeker2')}</li>
          <li>{t('legal.privacy.seeker3')}</li>
          <li>{t('legal.privacy.seeker4')}</li>
          <li>{t('legal.privacy.seeker5')}</li>
          <li>{t('legal.privacy.seeker6')}</li>
        </ul>

        <h3 className="text-xl font-semibold mt-6 mb-3">{t('legal.privacy.s2_3Title')}</h3>
        <p className="vscode-text-secondary mb-4">{t('legal.privacy.s2_3Intro')}</p>
        <ul className="list-disc list-inside vscode-text-secondary mb-4 space-y-2 ml-4">
          <li>{t('legal.privacy.emp1')}</li>
          <li>{t('legal.privacy.emp2')}</li>
          <li>{t('legal.privacy.emp3')}</li>
          <li>{t('legal.privacy.emp4')}</li>
          <li>{t('legal.privacy.emp5')}</li>
          <li>{t('legal.privacy.emp6')}</li>
          <li>{t('legal.privacy.emp7')}</li>
          <li>{t('legal.privacy.emp8')}</li>
        </ul>

        <h3 className="text-xl font-semibold mt-6 mb-3">{t('legal.privacy.s2_4Title')}</h3>
        <p className="vscode-text-secondary mb-4">{t('legal.privacy.s2_4Intro')}</p>
        <ul className="list-disc list-inside vscode-text-secondary mb-4 space-y-2 ml-4">
          <li>{t('legal.privacy.match1')}</li>
          <li>{t('legal.privacy.match2')}</li>
          <li>{t('legal.privacy.match3')}</li>
          <li>{t('legal.privacy.match4')}</li>
        </ul>

        {/* Section 3 */}
        <h2 className="text-2xl font-semibold mt-8 mb-4">{t('legal.privacy.s3Title')}</h2>
        <p className="vscode-text-secondary mb-4">{t('legal.privacy.s3Intro')}</p>
        <ul className="list-disc list-inside vscode-text-secondary mb-4 space-y-2 ml-4">
          <li>{t('legal.privacy.ai1')}</li>
          <li>{t('legal.privacy.ai2')}</li>
          <li>{t('legal.privacy.ai3')}</li>
          <li>{t('legal.privacy.ai4')}</li>
        </ul>
        <p className="vscode-text-secondary mb-4">{t('legal.privacy.s3Safeguard')}</p>

        {/* Section 4 */}
        <h2 className="text-2xl font-semibold mt-8 mb-4">{t('legal.privacy.s4Title')}</h2>
        
        <h3 className="text-xl font-semibold mt-6 mb-3">{t('legal.privacy.s4_1Title')}</h3>
        <p className="vscode-text-secondary mb-4">{t('legal.privacy.s4_1Intro')}</p>

        <h3 className="text-xl font-semibold mt-6 mb-3">{t('legal.privacy.s4_2Title')}</h3>
        <p className="vscode-text-secondary mb-4">{t('legal.privacy.s4_2Intro')}</p>
        <ul className="list-disc list-inside vscode-text-secondary mb-4 space-y-2 ml-4">
          <li>{t('legal.privacy.s4_2_1')}</li>
          <li>{t('legal.privacy.s4_2_2')}</li>
          <li>{t('legal.privacy.s4_2_3')}</li>
          <li>{t('legal.privacy.s4_2_4')}</li>
          <li>{t('legal.privacy.s4_2_5')}</li>
        </ul>
        <p className="vscode-text-secondary mb-4">{t('legal.privacy.s4_2Note')}</p>

        <h3 className="text-xl font-semibold mt-6 mb-3">{t('legal.privacy.s4_3Title')}</h3>
        <p className="vscode-text-secondary mb-4">{t('legal.privacy.s4_3Intro')}</p>

        <h3 className="text-xl font-semibold mt-6 mb-3">{t('legal.privacy.s4_4Title')}</h3>
        <p className="vscode-text-secondary mb-4">{t('legal.privacy.s4_4Intro')}</p>

        {/* Section 5 */}
        <h2 className="text-2xl font-semibold mt-8 mb-4">{t('legal.privacy.s5Title')}</h2>
        <p className="vscode-text-secondary mb-4">{t('legal.privacy.s5Intro')}</p>
        <ul className="list-disc list-inside vscode-text-secondary mb-4 space-y-2 ml-4">
          <li>{t('legal.privacy.sec1')}</li>
          <li>{t('legal.privacy.sec2')}</li>
          <li>{t('legal.privacy.sec3')}</li>
          <li>{t('legal.privacy.sec4')}</li>
          <li>{t('legal.privacy.sec5')}</li>
          <li>{t('legal.privacy.sec6')}</li>
        </ul>
        <p className="vscode-text-secondary mb-4">{t('legal.privacy.s5Note')}</p>

        {/* Section 6 */}
        <h2 className="text-2xl font-semibold mt-8 mb-4">{t('legal.privacy.s6Title')}</h2>
        
        <h3 className="text-xl font-semibold mt-6 mb-3">{t('legal.privacy.s6_1Title')}</h3>
        <p className="vscode-text-secondary mb-4">{t('legal.privacy.s6_1Intro')}</p>
        <ul className="list-disc list-inside vscode-text-secondary mb-4 space-y-2 ml-4">
          <li>{t('legal.privacy.gdprR1')}</li>
          <li>{t('legal.privacy.gdprR2')}</li>
          <li>{t('legal.privacy.gdprR3')}</li>
          <li>{t('legal.privacy.gdprR4')}</li>
          <li>{t('legal.privacy.gdprR5')}</li>
          <li>{t('legal.privacy.gdprR6')}</li>
          <li>{t('legal.privacy.gdprR7')}</li>
        </ul>

        <h3 className="text-xl font-semibold mt-6 mb-3">{t('legal.privacy.s6_2Title')}</h3>
        <p className="vscode-text-secondary mb-4">{t('legal.privacy.s6_2Intro')}</p>
        <ul className="list-disc list-inside vscode-text-secondary mb-4 space-y-2 ml-4">
          <li>{t('legal.privacy.ccpaR1')}</li>
          <li>{t('legal.privacy.ccpaR2')}</li>
          <li>{t('legal.privacy.ccpaR3')}</li>
          <li>{t('legal.privacy.ccpaR4')}</li>
        </ul>

        <h3 className="text-xl font-semibold mt-6 mb-3">{t('legal.privacy.s6_3Title')}</h3>
        <p className="vscode-text-secondary mb-4">
          {t('legal.privacy.s6_3Intro')} <a href="mailto:support@gograbjob.com" className="text-[var(--vscode-text-link)] hover:opacity-90">support@gograbjob.com</a>. {t('legal.privacy.s6_3Within')}
        </p>

        {/* Section 7 */}
        <h2 className="text-2xl font-semibold mt-8 mb-4">{t('legal.privacy.s7Title')}</h2>
        <p className="vscode-text-secondary mb-4">{t('legal.privacy.s7Intro')}</p>
        <ul className="list-disc list-inside vscode-text-secondary mb-4 space-y-2 ml-4">
          <li>{t('legal.privacy.cookie1')}</li>
          <li>{t('legal.privacy.cookie2')}</li>
          <li>{t('legal.privacy.cookie3')}</li>
          <li>{t('legal.privacy.cookie4')}</li>
        </ul>
        <p className="vscode-text-secondary mb-4">
          {t('legal.privacy.s7CookieNote')} <a href="/cookie-policy" className="text-[var(--vscode-text-link)] hover:opacity-90">{t('legal.privacy.cookiePolicyLink')}</a>.
        </p>

        {/* Section 8 */}
        <h2 className="text-2xl font-semibold mt-8 mb-4">{t('legal.privacy.s8Title')}</h2>
        <p className="vscode-text-secondary mb-4">{t('legal.privacy.s8Intro')}</p>
        <ul className="list-disc list-inside vscode-text-secondary mb-4 space-y-2 ml-4">
          <li>{t('legal.privacy.retain1')}</li>
          <li>{t('legal.privacy.retain2')}</li>
          <li>{t('legal.privacy.retain3')}</li>
          <li>{t('legal.privacy.retain4')}</li>
        </ul>

        {/* Section 9 */}
        <h2 className="text-2xl font-semibold mt-8 mb-4">{t('legal.privacy.s9Title')}</h2>
        <p className="vscode-text-secondary mb-4">{t('legal.privacy.s9Intro')}</p>
        <ul className="list-disc list-inside vscode-text-secondary mb-4 space-y-2 ml-4">
          <li>{t('legal.privacy.transfer1')}</li>
          <li>{t('legal.privacy.transfer2')}</li>
          <li>{t('legal.privacy.transfer3')}</li>
        </ul>

        {/* Section 10 */}
        <h2 className="text-2xl font-semibold mt-8 mb-4">{t('legal.privacy.s10Title')}</h2>
        
        <h3 className="text-xl font-semibold mt-6 mb-3">{t('legal.privacy.s10_1Title')}</h3>
        <p className="vscode-text-secondary mb-4">{t('legal.privacy.s10_1Intro')}</p>
        <ul className="list-disc list-inside vscode-text-secondary mb-4 space-y-2 ml-4">
          <li>{t('legal.privacy.cand1')}</li>
          <li>{t('legal.privacy.cand2')}</li>
          <li>{t('legal.privacy.cand3')}</li>
          <li>{t('legal.privacy.cand4')}</li>
          <li>{t('legal.privacy.cand5')}</li>
          <li>{t('legal.privacy.cand6')}</li>
        </ul>

        <h3 className="text-xl font-semibold mt-6 mb-3">{t('legal.privacy.s10_2Title')}</h3>
        <p className="vscode-text-secondary mb-4">{t('legal.privacy.s10_2Intro')}</p>
        <ul className="list-disc list-inside vscode-text-secondary mb-4 space-y-2 ml-4">
          <li>{t('legal.privacy.acc1')}</li>
          <li>{t('legal.privacy.acc2')}</li>
          <li>{t('legal.privacy.acc3')}</li>
          <li>{t('legal.privacy.acc4')}</li>
          <li>{t('legal.privacy.acc5')}</li>
        </ul>

        <h3 className="text-xl font-semibold mt-6 mb-3">{t('legal.privacy.s10_3Title')}</h3>
        <p className="vscode-text-secondary mb-4">{t('legal.privacy.s10_3Intro')}</p>
        <ul className="list-disc list-inside vscode-text-secondary mb-4 space-y-2 ml-4">
          <li>{t('legal.privacy.prob1')}</li>
          <li>{t('legal.privacy.prob2')}</li>
          <li>{t('legal.privacy.prob3')}</li>
          <li>{t('legal.privacy.prob4')}</li>
          <li>{t('legal.privacy.prob5')}</li>
        </ul>

        <h3 className="text-xl font-semibold mt-6 mb-3">{t('legal.privacy.s10_4Title')}</h3>
        <p className="vscode-text-secondary mb-4">{t('legal.privacy.s10_4Intro')}</p>
        <ul className="list-disc list-inside vscode-text-secondary mb-4 space-y-2 ml-4">
          <li>{t('legal.privacy.comm1')}</li>
          <li>{t('legal.privacy.comm2')}</li>
          <li>{t('legal.privacy.comm3')}</li>
          <li>{t('legal.privacy.comm4')}</li>
        </ul>

        <h3 className="text-xl font-semibold mt-6 mb-3">{t('legal.privacy.s10_5Title')}</h3>
        <p className="vscode-text-secondary mb-4">{t('legal.privacy.s10_5Intro')}</p>
        <ul className="list-disc list-inside vscode-text-secondary mb-4 space-y-2 ml-4">
          <li>{t('legal.privacy.breach1')}</li>
          <li>{t('legal.privacy.breach2')}</li>
          <li>{t('legal.privacy.breach3')}</li>
          <li>{t('legal.privacy.breach4')}</li>
        </ul>

        <h3 className="text-xl font-semibold mt-6 mb-3">{t('legal.privacy.s10_6Title')}</h3>
        <p className="vscode-text-secondary mb-4">{t('legal.privacy.s10_6Intro')}</p>
        <ul className="list-disc list-inside vscode-text-secondary mb-4 space-y-2 ml-4">
          <li>{t('legal.privacy.labor1')}</li>
          <li>{t('legal.privacy.labor2')}</li>
          <li>{t('legal.privacy.labor3')}</li>
          <li>{t('legal.privacy.labor4')}</li>
          <li>{t('legal.privacy.labor5')}</li>
          <li>{t('legal.privacy.labor6')}</li>
        </ul>

        <div className="mt-6 p-4 bg-orange-900/20 border-l-4 border-orange-500">
          <p className="vscode-text-secondary">{t('legal.privacy.violationWarning')}</p>
        </div>

        {/* Section 11 */}
        <h2 className="text-2xl font-semibold mt-8 mb-4">{t('legal.privacy.s11Title')}</h2>
        
        <h3 className="text-xl font-semibold mt-6 mb-3">{t('legal.privacy.s11_1Title')}</h3>
        <p className="vscode-text-secondary mb-4">{t('legal.privacy.s11_1Intro')}</p>
        <ul className="list-disc list-inside vscode-text-secondary mb-4 space-y-2 ml-4">
          <li>{t('legal.privacy.vis1')}</li>
          <li>{t('legal.privacy.vis2')}</li>
          <li>{t('legal.privacy.vis3')}</li>
          <li>{t('legal.privacy.vis4')}</li>
        </ul>

        <h3 className="text-xl font-semibold mt-6 mb-3">{t('legal.privacy.s11_2Title')}</h3>
        <p className="vscode-text-secondary mb-4">{t('legal.privacy.s11_2Intro')}</p>
        <ul className="list-disc list-inside vscode-text-secondary mb-4 space-y-2 ml-4">
          <li>{t('legal.privacy.app1')}</li>
          <li>{t('legal.privacy.app2')}</li>
          <li>{t('legal.privacy.app3')}</li>
          <li>{t('legal.privacy.app4')}</li>
        </ul>

        {/* Section 12 */}
        <h2 className="text-2xl font-semibold mt-8 mb-4">{t('legal.privacy.s12Title')}</h2>
        <p className="vscode-text-secondary mb-4">{t('legal.privacy.s12Intro')}</p>

        {/* Section 13 */}
        <h2 className="text-2xl font-semibold mt-8 mb-4">{t('legal.privacy.s13Title')}</h2>
        <p className="vscode-text-secondary mb-4">{t('legal.privacy.s13Intro')}</p>

        {/* Section 14 */}
        <h2 className="text-2xl font-semibold mt-8 mb-4">{t('legal.privacy.s14Title')}</h2>
        <p className="vscode-text-secondary mb-4">{t('legal.privacy.s14Intro')}</p>
        <ul className="list-disc list-inside vscode-text-secondary mb-4 space-y-2 ml-4">
          <li>{t('legal.privacy.s14_1')}</li>
          <li>{t('legal.privacy.s14_2')}</li>
          <li>{t('legal.privacy.s14_3')}</li>
        </ul>
        <p className="vscode-text-secondary mb-4">{t('legal.privacy.s14_5')}</p>

        {/* Section 15 */}
        <h2 className="text-2xl font-semibold mt-8 mb-4">{t('legal.privacy.s15Title')}</h2>
        <p className="vscode-text-secondary mb-4">
          {t('legal.privacy.s15Intro')}
        </p>
        <div className=" p-6-lg mb-4">
          <p className="vscode-text-secondary mb-2"><strong>{t('legal.privacy.contactTitle')}</strong></p>
          <p className="vscode-text-secondary mb-2">
            {t('legal.privacy.contactEmail')} <a href="mailto:support@gograbjob.com" className="text-[var(--vscode-text-link)] hover:opacity-90">support@gograbjob.com</a>
          </p>
          <p className="vscode-text-secondary mb-2">
            {t('legal.privacy.contactSupport')} <a href="mailto:support@gograbjob.com" className="text-[var(--vscode-text-link)] hover:opacity-90">support@gograbjob.com</a>
          </p>
          <p className="vscode-text-secondary">
            {t('legal.privacy.contactWebsite')} <a href="https://www.gograbjob.com" className="text-[var(--vscode-text-link)] hover:opacity-90">www.gograbjob.com</a>
          </p>
        </div>

        {/* Data Protection Officer */}
        <div className="mt-8 p-6  border border-blue-700/30-lg">
          <h3 className="text-xl font-semibold mb-3">{t('legal.privacy.dpoTitle')}</h3>
          <p className="vscode-text-secondary">{t('legal.privacy.dpoIntro')}</p>
          <p className="vscode-text-secondary mt-2">
            {t('legal.privacy.dpoEmailLabel')} <a href="mailto:support@gograbjob.com" className="text-[var(--vscode-text-link)] hover:opacity-90">support@gograbjob.com</a>
          </p>
        </div>

        {/* Supervisory Authority */}
        <div className="mt-6 p-4 -lg">
          <p className="text-sm vscode-text-secondary">
            <strong>{t('legal.privacy.supervisoryTitle')}</strong> {t('legal.privacy.supervisoryIntro')}
          </p>
        </div>

        {/* Final Note */}
        <div className="mt-8 p-6  ">
          <p className="vscode-text-secondary"><strong>{t('legal.privacy.finalNote')}</strong></p>
        </div>
      </div>

      {/* Related Policies Navigation */}
      <PolicyNavigation currentPolicy="privacy" onNavigate={onNavigate} />
    </div>
  );
};

export default PrivacyPolicyContent;
